import React from 'react';
import { Grid, Stepper, Step, StepLabel, StepButton, Box } from '@mui/material';
import { AppContainer, MainContentContainer, BodyContainer } from '../../resources/styling/appStyle';
import { SideMenu } from '../layouts/SideMenu';
import { Header } from '../layouts/Header';
import { sa, Card, PageTitle, PillButton } from '../superAdminUi';

// One page frame + chrome for every create and edit wizard, on the shared Super Admin design
// system: the app shell (side menu, header), a stepper (completed steps carry a check and jump
// back on click, the current step is teal-filled, future steps are muted), the two-column
// content/summary layout that collapses on small screens, and the Back / Continue (or final
// action) row with its gating. It owns none of the data — steps, handlers and the summary are
// passed in — so create and edit surfaces stay identical in look and feel.
const stepperSx = {
  mb: 3,
  '& .MuiStepIcon-root': { color: sa.color.mist },
  '& .MuiStepIcon-root.Mui-active': { color: sa.color.teal },
  '& .MuiStepIcon-root.Mui-completed': { color: sa.color.teal },
  '& .MuiStepLabel-label': { fontFamily: sa.font.family, fontSize: 13, color: sa.color.textGray },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: sa.font.bold, color: sa.color.text },
  '& .MuiStepLabel-label.Mui-completed': { color: sa.color.text },
};

export default function WizardShell({
  title,
  steps,
  activeStep,
  onStepSelect,
  summary,
  banner,
  hideFooter = false,
  onBack,
  onNext,
  nextLabel = 'Continue',
  nextDisabled = false,
  finalAction = null,
  children,
}) {
  const isLastStep = activeStep === steps.length - 1;

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          {title && (
            <Box sx={{ mb: 2 }}>
              <PageTitle>{title}</PageTitle>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Card>
                <Stepper activeStep={activeStep} alternativeLabel sx={stepperSx}>
                  {steps.map((step, index) => {
                    const label = typeof step === 'string' ? step : step.label;
                    const completed = index < activeStep;
                    return (
                      <Step key={label} completed={completed}>
                        {completed && onStepSelect ? (
                          <StepButton onClick={() => onStepSelect(index)}>{label}</StepButton>
                        ) : (
                          <StepLabel>{label}</StepLabel>
                        )}
                      </Step>
                    );
                  })}
                </Stepper>

                {banner}

                <Box>{children}</Box>

                {!hideFooter && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <PillButton
                      type="button"
                      $variant="outline"
                      $size="lg"
                      onClick={onBack}
                      disabled={activeStep === 0}
                    >
                      Back
                    </PillButton>
                    {isLastStep ? (
                      finalAction
                    ) : (
                      <PillButton type="button" $size="lg" onClick={onNext} disabled={nextDisabled}>
                        {nextLabel}
                      </PillButton>
                    )}
                  </Box>
                )}
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              {summary}
            </Grid>
          </Grid>
        </MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );
}
